import * as React from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { useNavigate, useParams } from 'react-router-dom';
import { notify } from 'services';
import type { UpdateServiceOrderPayload } from 'contracts/service-orders';
import { useAuth } from 'hooks';
import {
  useFetchServiceOrder,
  useUpdateServiceOrder,
} from 'hooks/api/service-orders';
import { useUpdateServiceOrderProduct } from 'hooks/api/service-order-products';
import { Scaffold } from 'layouts';
import {
  ConfirmationDialog,
  type ConfirmationDialogRef,
} from 'components/ui/confirmation-dialog';

import {
  CanceledOrderStatusBox,
  ServiceOrderProductsTable,
  TasksTable,
  ObservationsList,
  Header,
  UpdateServiceOrderForm,
  type UpdateServiceOrderFormRef,
  UpdateWorkedQuantityDrawer,
  type UpdateWorkedQuantityDrawerRef,
} from './components';
import { compareFormDataAndServiceOrder } from './utils';

export const ServiceOrderDetailPage = () => {
  const { id: serviceOrderId } = useParams<{ id: string }>();
  const { profile } = useAuth();
  const navigate = useNavigate();

  const isAdminOrWarehouseMember = React.useMemo(() => {
    if (!profile?.type) return false;

    return ['admin', 'warehouseMember'].includes(profile.type);
  }, [profile?.type]);

  const { fetchServiceOrder } = useFetchServiceOrder();
  const { updateServiceOrder } = useUpdateServiceOrder();
  const { updateServiceOrderProduct } = useUpdateServiceOrderProduct();

  const navigateBackConfirmationDialogRef = React.useRef<ConfirmationDialogRef>(null);
  const updateServiceOrderFormRef = React.useRef<UpdateServiceOrderFormRef>(null);
  const updateWorkedQuantityDrawerRef = React.useRef<UpdateWorkedQuantityDrawerRef>(null);

  const queryClient = useQueryClient();

  const serviceOrderQuery = useQuery({
    queryKey: ['fetch-service-order', serviceOrderId],
    queryFn: () => fetchServiceOrder(+serviceOrderId!),
    refetchOnWindowFocus: false,
  });

  const serviceOrderProducts = React.useMemo(() => {
    if (!serviceOrderQuery?.data) return [];
    const order = serviceOrderQuery.data;

    return order.canceledAt
      ? order.canceledProducts
      : order.products;

  }, [serviceOrderQuery?.data]);

  const updateServiceMutation = useMutation({
    mutationFn: updateServiceOrder,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['fetch-service-order']);
      notify('success', 'Ordem de serviço atualizada com sucesso!');
    },
    onError: () => {
      notify('error', 'Erro ao atualizar ordem de serviço');
    },
  });

  const updateServiceOrderProductMutation = useMutation({
    mutationFn: updateServiceOrderProduct,
    onSuccess: async () => {
      await queryClient.invalidateQueries(['fetch-service-order']);
      notify('success', 'Quantidade liberada atualizada com sucesso!');
    },
    onError: () => {
      notify('error', 'Erro ao atualizar quantidade liberada.');
    },
    onSettled: () => {
      updateWorkedQuantityDrawerRef.current?.close();
    },
  });

  const onUpdateFormSubmit = React.useCallback((payload: UpdateServiceOrderPayload) => {
    updateServiceMutation.mutate({
      serviceOrderId: +serviceOrderId!,
      payload,
    });
  }, [serviceOrderId]);

  const navigateBack = () => navigate(-1);

  const onNavigateBack = React.useCallback(async () => {
    const formData = updateServiceOrderFormRef.current?.getFormData();
    const serviceOrder = serviceOrderQuery.data;

    if (!serviceOrder || !formData) {
      return navigateBack();
    }

    const { changedFields } = compareFormDataAndServiceOrder(
      formData,
      serviceOrder,
    );

    if (!changedFields.length) {
      return navigateBack();
    }

    const confirmation = await navigateBackConfirmationDialogRef.current?.openDialog();

    if (confirmation === 'confirmed') {
      return navigateBack();
    }
  }, [serviceOrderQuery?.data]);

  return (
    <Scaffold>
      <section className={'mb-20'}>
        <Header
          loading={serviceOrderQuery.isLoading}
          serviceOrder={serviceOrderQuery.data}
          onClickGoBack={onNavigateBack}
          enableActionsDropdown={isAdminOrWarehouseMember}
        />
        {serviceOrderQuery.data?.currentStatus === 'cancelado' && (
          <CanceledOrderStatusBox order={serviceOrderQuery.data!} className={'mt-5'} />
        )}
        <UpdateWorkedQuantityDrawer
          ref={updateWorkedQuantityDrawerRef}
          onSubmit={updateServiceOrderProductMutation.mutate}
          loading={updateServiceOrderProductMutation.isLoading}
        />
        <div className={'mt-6'}>
          {!serviceOrderQuery.data && <UpdateServiceOrderForm.Skeleton />}
          {serviceOrderQuery.data && <UpdateServiceOrderForm
            ref={updateServiceOrderFormRef}
            serviceOrder={serviceOrderQuery.data}
            loading={serviceOrderQuery.isLoading || updateServiceMutation.isLoading}
            onSubmit={onUpdateFormSubmit}
            enableDataEditing={isAdminOrWarehouseMember && serviceOrderQuery.data.currentStatus !== 'cancelado'}
          />}
        </div>
        <h1 className={'text-lg text-gray-600 mt-6'}>
          Produtos a serem inspecionados
        </h1>
        {!serviceOrderQuery.data && <ServiceOrderProductsTable.Skeleton />}
        {serviceOrderQuery.data && <ServiceOrderProductsTable
          className={'mt-4'}
          serviceOrderProducts={serviceOrderProducts}
          onUpdateWorkedQuantity={updateWorkedQuantityDrawerRef.current?.open}
          showActionsDropdown={isAdminOrWarehouseMember}
        />}
        <h1 className={'text-lg text-gray-600 mt-6'}>Trabalhos</h1>
        <TasksTable
          className={'mt-4'}
          loading={serviceOrderQuery.isLoading}
          data={serviceOrderQuery.data?.tasks}
          showDeleteTaskButton={isAdminOrWarehouseMember}
        />
        <ObservationsList
          className={'mt-6'}
          loading={serviceOrderQuery.isLoading}
          data={serviceOrderQuery.data?.observations}
        />
      </section>
      <ConfirmationDialog
        ref={navigateBackConfirmationDialogRef}
        title={'Atenção'}
        message={'Existem alterações não salvas. Deseja realmente voltar?'}
      />
    </Scaffold>
  );
};
